/**
 * Everything waiting for a yes: access requests (from the chat), push
 * notifications other people wrote, Connect changes from roles that need an
 * administrator's approval, and changes to the website's blog. One list, one
 * badge. Non-namespaced like every other module here.
 */
const notifyError = (commit, message) =>
  commit("NOTIFICATION_MESSAGE", { status: -1, title: "Approvals", message });

const form = (obj) => {
  const p = new URLSearchParams();
  Object.keys(obj).forEach((k) => { if (obj[k] !== undefined && obj[k] !== null) p.append(k, obj[k]); });
  return p;
};

const empty = () => ({ approver: false, pending: [], mine: [] });

const state = () => ({
  loaded: false,
  requests: empty(),
  notifications: empty(),
  changes: empty(), // Connect cards sent for an administrator's yes (assistant/approvals)
  blog: empty(), // website blog changes from anyone but an administrator (blog/requests); the decisions live in blog.js
});

const mutations = {
  SET_APPROVAL_REQUESTS(s, d) { s.requests = { approver: !!d.approver, pending: d.pending || [], mine: d.mine || [] }; s.loaded = true; },
  SET_APPROVAL_NOTIFICATIONS(s, d) { s.notifications = { approver: !!d.approver, pending: d.pending || [], mine: d.mine || [] }; s.loaded = true; },
  SET_APPROVAL_CHANGES(s, d) { s.changes = { approver: !!d.approver, pending: d.pending || [], mine: [] }; s.loaded = true; },
  SET_APPROVAL_BLOG(s, d) { s.blog = { approver: !!d.approver, pending: d.pending || [], mine: d.mine || [] }; s.loaded = true; },
  RESET_APPROVALS(s) { Object.assign(s, state()); },
};

const getters = {
  approvalsCount: (s) => s.requests.pending.length + s.notifications.pending.length + s.changes.pending.length + s.blog.pending.length,
};

const actions = {
  /** Every list; each endpoint is optional so an API without one of them still works. */
  async loadApprovals({ commit }) {
    try {
      const { data } = await window.$http.axios.get("chat/approvals", { silent: true });
      if (data.status) commit("SET_APPROVAL_REQUESTS", data);
    } catch (e) { /* chat not installed */ }
    try {
      const { data } = await window.$http.axios.get("notifications/pending", { silent: true });
      if (data.status) commit("SET_APPROVAL_NOTIFICATIONS", data);
    } catch (e) { /* approvals not installed */ }
    try {
      const { data } = await window.$http.axios.get("assistant/approvals", { silent: true });
      if (data.status) commit("SET_APPROVAL_CHANGES", data);
    } catch (e) { /* no assistant on this API, or not given it */ }
    try {
      const { data } = await window.$http.axios.get("blog/requests", { silent: true });
      if (data.status) commit("SET_APPROVAL_BLOG", data);
    } catch (e) { /* no website blog on this API, or the role does not hold blog.view */ }
  },

  /**
   * An administrator's yes or no on a Connect change. Yes runs it as the person who asked, with their
   * permissions and stores; they see the result (or the note) in their own chat with Connect.
   */
  async decideChange({ commit, dispatch }, { id, decision, note }) {
    try {
      const { data } = await window.$http.axios.post("assistant/approve", form({ id, decision, note: note || "" }));
      dispatch("loadApprovals");
      if (data.status) {
        commit("NOTIFICATION_MESSAGE", { status: 1, title: "Approvals", message: data.message || (decision === "approve" ? "Approved." : "Not approved.") });
        return true;
      }
      notifyError(commit, data.message || "Could not do that."); return false;
    } catch (e) { notifyError(commit, "Could not do that just now."); return false; }
  },

  async approveNotification({ commit, dispatch }, hash) {
    try {
      const { data } = await window.$http.axios.post("notifications/approve", form({ hash }));
      if (data.status) {
        commit("NOTIFICATION_MESSAGE", { status: 1, title: "Approvals", message: "Approved — it goes out to customers at its scheduled time." });
        dispatch("loadApprovals");
        return true;
      }
      notifyError(commit, data.message); return false;
    } catch (e) { notifyError(commit, "Could not approve."); return false; }
  },

  /** Delete a draft (never a sent one — the API refuses that). */
  async removeNotification({ commit, dispatch }, hash) {
    try {
      const { data } = await window.$http.axios.delete("notifications/delete/" + hash);
      if (data.status) { commit("NOTIFICATION_MESSAGE", { status: 1, title: "Approvals", message: "Removed." }); dispatch("loadApprovals"); return true; }
      notifyError(commit, data.message); return false;
    } catch (e) { notifyError(commit, "Could not remove it."); return false; }
  },

  async rejectNotification({ commit, dispatch }, { hash, note }) {
    try {
      const { data } = await window.$http.axios.post("notifications/reject", form({ hash, note: note || "" }));
      if (data.status) {
        commit("NOTIFICATION_MESSAGE", { status: 1, title: "Approvals", message: "Rejected. The writer has your note." });
        dispatch("loadApprovals");
        return true;
      }
      notifyError(commit, data.message); return false;
    } catch (e) { notifyError(commit, "Could not reject."); return false; }
  },
};

export default { state, getters, mutations, actions };
